import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, ShieldX, Clock, CheckCircle2,
  AlertTriangle, Loader2, CreditCard, RefreshCw, Users,
  ChevronRight, Link2, IndianRupee, XCircle,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LEVEL_LABELS = {
  national_partner: "National Partner",
  regional_partner: "Regional Partner",
  zonal_partner: "Zonal Partner",
  sales_manager: "Sales Manager",
  sales_partner: "Sales Partner",
};

// ── Journey Stepper ────────────────────────────────────────────────────────────

const VERIFIED_STATES = ["verified", "active", "approved"];

function JourneyTracker({ kycStatus, panVerified, bankVerified }) {
  const isVerified = VERIFIED_STATES.includes(kycStatus);
  const hasActivity = panVerified || bankVerified || ["admin_review", "rejected", "query_raised"].includes(kycStatus);

  const steps = [
    { label: "Registration",    state: "done" },
    { label: "Document Checks", state: isVerified ? "done" : hasActivity ? "active" : "active" },
    { label: "Account Active",  state: isVerified ? "done" : "pending" },
  ];

  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="relative flex items-start">
        {/* Base connector */}
        <div className="absolute top-[18px] left-[calc(16.7%+18px)] right-[calc(16.7%+18px)] h-[2px] bg-border z-0" />
        {/* Progress fill */}
        <div className={`absolute top-[18px] h-[2px] z-0 transition-all duration-700 ${
          isVerified  ? "bg-emerald-500/50 left-[calc(16.7%+18px)] right-[calc(16.7%+18px)]" :
          hasActivity ? "bg-primary/40 left-[calc(16.7%+18px)] right-[calc(50%+0px)]" :
                        "bg-primary/20 left-[calc(16.7%+18px)] w-0"
        }`} />

        {steps.map((step, i) => (
          <div key={i} className={`flex-1 flex flex-col items-center z-10 ${i === 0 ? "" : i === 2 ? "" : ""}`}>
            <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all ${
              step.state === "done"   ? "bg-emerald-500/10 border-emerald-500" :
              step.state === "active" ? "bg-primary/10 border-primary" :
                                        "bg-muted border-border"
            }`}>
              {step.state === "done" ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600" strokeWidth={2} />
              ) : step.state === "active" ? (
                <span className="w-2.5 h-2.5 rounded-full bg-primary block animate-pulse" />
              ) : (
                <span className="text-[10px] text-muted-foreground font-bold">{i + 1}</span>
              )}
            </div>
            <span className={`mt-2 text-[11px] font-medium text-center ${
              step.state === "done"   ? "text-emerald-600" :
              step.state === "active" ? "text-primary" :
                                        "text-muted-foreground"
            }`}>{step.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Overall Status Banner ───────────────────────────────────────────────────────

function StatusBanner({ kycStatus, kycData }) {
  const configs = {
    verified:     { bg: "bg-emerald-500/8 border-emerald-500/25", Icon: ShieldCheck, iconCls: "text-emerald-500", title: "KYC Verified — Account Active", sub: "Your identity has been verified and your partner account is fully active. Start sharing your referral links to earn commissions." },
    active:       { bg: "bg-emerald-500/8 border-emerald-500/25", Icon: ShieldCheck, iconCls: "text-emerald-500", title: "KYC Verified — Account Active", sub: "Your identity has been verified and your partner account is fully active." },
    approved:     { bg: "bg-emerald-500/8 border-emerald-500/25", Icon: ShieldCheck, iconCls: "text-emerald-500", title: "KYC Approved — Account Active", sub: "Your KYC has been approved by our team and your partner account is fully active." },
    admin_review: { bg: "bg-amber-500/8 border-amber-500/25",   Icon: Clock,       iconCls: "text-amber-500",   title: "Under Manual Review",          sub: kycData?.kyc_review_reason || "Your documents are being reviewed by our compliance team. This usually takes 1–2 business days." },
    rejected:     { bg: "bg-red-500/8 border-red-500/25",       Icon: ShieldX,     iconCls: "text-red-500",     title: "KYC Application Rejected",     sub: kycData?.kyc_reject_reason || "Your application was rejected. Please review the reason and reapply." },
    query_raised: { bg: "bg-blue-500/8 border-blue-500/25",     Icon: AlertTriangle,iconCls:"text-blue-500",    title: "Query Raised",                 sub: "Our compliance team has a query about your application. Please contact support to respond." },
    default:      { bg: "bg-primary/8 border-primary/25",       Icon: ShieldAlert, iconCls: "text-primary",    title: "KYC Verification Pending",     sub: "Complete your PAN and Bank account verification below to activate your partner account." },
  };
  const cfg = configs[kycStatus] || configs.default;
  const { bg, Icon, iconCls, title, sub } = cfg;

  return (
    <div className={`border rounded-2xl p-5 flex items-start gap-4 ${bg}`} data-testid="kyc-status-banner">
      <div className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 bg-background border border-border`}>
        <Icon className={`w-5 h-5 ${iconCls}`} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm mb-0.5 ${iconCls}`}>{title}</div>
        <p className="text-[12px] text-muted-foreground leading-relaxed">{sub}</p>
        {kycData?.kyc_reject_score != null && kycStatus === "rejected" && (
          <div className="mt-1.5 text-[11px] text-red-400/70">
            Name similarity score: {Math.round(kycData.kyc_reject_score * 100)}%
          </div>
        )}
        {kycData?.kyc_review_score != null && kycStatus === "admin_review" && (
          <div className="mt-1.5 text-[11px] text-amber-400/70">
            Name similarity score: {Math.round(kycData.kyc_review_score * 100)}% — borderline match under review
          </div>
        )}
      </div>
    </div>
  );
}

// ── Verification Cards ──────────────────────────────────────────────────────────

function VerifyCard({ type, verified, hasDocs, value, loading, onVerify }) {
  const isPan  = type === "pan";
  const Icon   = isPan ? CreditCard : ShieldCheck;
  const label  = isPan ? "PAN Verification" : "Bank Verification";
  const testId = isPan ? "kyc-status-verify-pan-btn" : "kyc-status-verify-bank-btn";

  return (
    <div className={`bg-card border rounded-xl p-4 flex flex-col gap-3 transition-all ${
      verified ? "border-emerald-500/30" : "border-border"
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-3.5 h-3.5 text-muted-foreground" strokeWidth={1.5} />
          <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">{label}</span>
        </div>
        {verified ? (
          <span className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
            <CheckCircle2 className="w-3 h-3" />Verified
          </span>
        ) : (
          <span className="flex items-center gap-1 text-[10px] text-amber-500">
            <ShieldAlert className="w-3 h-3" />Pending
          </span>
        )}
      </div>

      <div className="font-mono text-sm text-foreground tracking-wider min-h-[20px]">
        {value || <span className="text-muted-foreground">—</span>}
      </div>

      {verified ? (
        <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 mt-auto">
          <CheckCircle2 className="w-3 h-3" />Successfully verified
        </div>
      ) : hasDocs ? (
        <button
          onClick={onVerify}
          disabled={loading}
          data-testid={testId}
          className="mt-auto flex items-center justify-center gap-2 py-2 bg-primary/10 border border-primary/30 text-primary text-[12px] font-medium rounded-lg hover:bg-primary/20 disabled:opacity-50 transition-all"
        >
          {loading ? <><Loader2 className="w-3 h-3 animate-spin" />Verifying…</> : `Verify ${isPan ? "PAN" : "Bank Account"}`}
        </button>
      ) : (
        <div className="text-[11px] text-muted-foreground mt-auto">
          {isPan ? "No PAN on record. Contact support." : "Update bank details in Profile & Bank first."}
        </div>
      )}
    </div>
  );
}

// ── What's Next ─────────────────────────────────────────────────────────────────

function WhatNext({ kycStatus, panVerified, bankVerified, hasPan, hasBank }) {
  if (VERIFIED_STATES.includes(kycStatus)) {
    return (
      <div className="bg-card border border-emerald-500/20 rounded-2xl p-5" data-testid="kyc-whats-next">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-semibold text-foreground">You're all set!</span>
        </div>
        <p className="text-[13px] text-muted-foreground mb-4">
          Your KYC is complete and your account is active. Here's how to get started:
        </p>
        <div className="space-y-2">
          {[
            { to: "/partner/links",        Icon: Link2,        label: "Share your referral links to start earning" },
            { to: "/partner/commissions",  Icon: IndianRupee,  label: "Track your earnings and pending commissions" },
            { to: "/partner/referrals",    Icon: Users,        label: "See all the firms you've referred" },
          ].map(({ to, Icon, label }) => (
            <Link key={to} to={to}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors group">
              <Icon className="w-3.5 h-3.5 text-primary" strokeWidth={1.5} />
              <span className="text-[13px] text-foreground group-hover:text-primary transition-colors">{label}</span>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground ml-auto" />
            </Link>
          ))}
        </div>
      </div>
    );
  }

  if (kycStatus === "admin_review") {
    return (
      <div className="bg-card border border-border rounded-2xl p-5" data-testid="kyc-whats-next">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-foreground">What happens next?</span>
        </div>
        <ol className="space-y-3 text-[13px] text-muted-foreground">
          {[
            "Our compliance team is reviewing your PAN and bank documents.",
            "The review usually takes 1–2 business days.",
            "You'll receive an email and WhatsApp notification once a decision is made.",
            "No action is required from you right now — just sit tight.",
          ].map((text, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-5 h-5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-600 text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
              <span className="leading-relaxed">{text}</span>
            </li>
          ))}
        </ol>
        <div className="mt-4 pt-4 border-t border-border">
          <p className="text-[11px] text-muted-foreground">If you haven't heard back after 2 business days, contact your account manager or support team.</p>
        </div>
      </div>
    );
  }

  if (kycStatus === "rejected") {
    return (
      <div className="bg-card border border-red-500/20 rounded-2xl p-5" data-testid="kyc-whats-next">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          <span className="text-sm font-semibold text-foreground">Steps to reapply</span>
        </div>
        <ol className="space-y-3 text-[13px] text-muted-foreground">
          {[
            "Review the rejection reason shown in the status card above.",
            "Go to Profile & Bank to correct your PAN number or bank account details.",
            "Once updated, contact your account manager or support team to request re-verification.",
            "Our team will re-process your application within 1–2 business days.",
          ].map((text, i) => (
            <li key={i} className="flex gap-3">
              <span className="shrink-0 w-5 h-5 rounded-full bg-red-500/10 border border-red-500/30 text-red-500 text-[10px] flex items-center justify-center font-bold">{i + 1}</span>
              <span className="leading-relaxed">{text}</span>
            </li>
          ))}
        </ol>
        <div className="mt-4 pt-4 border-t border-border flex gap-3 flex-wrap">
          <Link to="/partner/profile"
            className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground text-[12px] font-semibold rounded-lg hover:bg-primary/90 transition-colors">
            Update Profile & Bank <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    );
  }

  if (kycStatus === "query_raised") {
    return (
      <div className="bg-card border border-blue-500/20 rounded-2xl p-5" data-testid="kyc-whats-next">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-semibold text-foreground">Action Required</span>
        </div>
        <p className="text-[13px] text-muted-foreground leading-relaxed">
          Our compliance team has raised a query on your application. Please contact your account manager or support to provide the requested information.
        </p>
      </div>
    );
  }

  // Default: pending — show step-by-step what to do
  const panStep   = !panVerified  && hasPan;
  const bankStep  = !bankVerified && hasBank;
  const noDataStep = !hasPan && !hasBank;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="kyc-whats-next">
      <div className="flex items-center gap-2 mb-4">
        <ShieldAlert className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Complete your KYC</span>
      </div>
      {noDataStep ? (
        <div className="text-[13px] text-muted-foreground space-y-2">
          <p>Your PAN and bank details are not on file yet.</p>
          <Link to="/partner/profile"
            className="inline-flex items-center gap-1.5 mt-2 px-4 py-2 bg-primary text-primary-foreground text-[12px] font-semibold rounded-lg hover:bg-primary/90 transition-colors">
            Go to Profile & Bank <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <ol className="space-y-3 text-[13px] text-muted-foreground">
          {[
            ...(panStep ? [{
              done: false,
              text: "Verify your PAN number — click 'Verify PAN' in the verification card above.",
            }] : panVerified ? [{ done: true, text: "PAN verified." }] : []),
            ...(bankStep ? [{
              done: false,
              text: "Verify your bank account — click 'Verify Bank Account' in the verification card above.",
            }] : bankVerified ? [{ done: true, text: "Bank account verified." }] : []),
            {
              done: false,
              text: "Once both are verified, our system will automatically process your KYC decision.",
            },
          ].map((item, i) => (
            <li key={i} className="flex gap-3">
              <span className={`shrink-0 w-5 h-5 rounded-full text-[10px] flex items-center justify-center font-bold ${
                item.done
                  ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-600"
                  : "bg-primary/10 border border-primary/30 text-primary"
              }`}>
                {item.done ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
              </span>
              <span className={`leading-relaxed ${item.done ? "line-through text-muted-foreground/50" : ""}`}>{item.text}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ── Team KYC Table ──────────────────────────────────────────────────────────────

const KYC_STATUS_STYLES = {
  verified:     "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  active:       "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
  admin_review: "bg-amber-500/10  text-amber-600  border-amber-500/20",
  rejected:     "bg-red-500/10    text-red-600    border-red-500/20",
  query_raised: "bg-blue-500/10   text-blue-600   border-blue-500/20",
  pending:      "bg-muted         text-muted-foreground border-border",
  kyc_pending:  "bg-muted         text-muted-foreground border-border",
};

const KYC_STATUS_LABELS = {
  verified:     "Verified",
  active:       "Verified",
  admin_review: "Under Review",
  rejected:     "Rejected",
  query_raised: "Query",
  pending:      "Pending",
  kyc_pending:  "Pending",
};

function TeamKYC({ teamData }) {
  const [filter, setFilter] = useState("all");
  if (!teamData) return null;
  const { team, summary, can_view_team } = teamData;
  if (!can_view_team) return null;

  const FILTERS = [
    { key: "all",         label: "All",          count: summary.total },
    { key: "verified",    label: "Verified",      count: summary.verified },
    { key: "admin_review",label: "Under Review",  count: summary.admin_review },
    { key: "pending",     label: "Pending",       count: summary.pending },
    { key: "rejected",    label: "Rejected",      count: summary.rejected },
  ];

  const filtered = filter === "all"
    ? team
    : filter === "verified"
    ? team.filter(t => ["verified", "active"].includes(t.kyc_status))
    : filter === "pending"
    ? team.filter(t => ["pending", "kyc_pending", "query_raised"].includes(t.kyc_status))
    : team.filter(t => t.kyc_status === filter);

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="team-kyc-section">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" strokeWidth={1.5} />
          <span className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground">Team KYC Status</span>
          <span className="text-[11px] px-2 py-0.5 bg-muted rounded-full text-muted-foreground">{summary.total} partners</span>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-5 py-4 border-b border-border">
        {[
          { label: "Verified",    val: summary.verified,     cls: "text-emerald-600" },
          { label: "Under Review",val: summary.admin_review, cls: "text-amber-600" },
          { label: "Pending",     val: summary.pending,      cls: "text-muted-foreground" },
          { label: "Rejected",    val: summary.rejected,     cls: "text-red-500" },
        ].map(({ label, val, cls }) => (
          <div key={label} className="text-center p-2 rounded-xl bg-muted/50">
            <div className={`text-xl font-light font-serif ${cls}`}>{val}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 px-5 py-3 border-b border-border overflow-x-auto">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap transition-colors ${
              filter === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
            }`}>
            {f.label}
            {f.count > 0 && <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-background/30">{f.count}</span>}
          </button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground">No partners in this category</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="px-5 py-3 text-left text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">Partner</th>
                <th className="px-3 py-3 text-left text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">Level</th>
                <th className="px-3 py-3 text-center text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">PAN</th>
                <th className="px-3 py-3 text-center text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">Bank</th>
                <th className="px-5 py-3 text-left text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-medium">KYC Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(p => (
                <tr key={p.id} className="hover:bg-muted/30 transition-colors" data-testid={`team-kyc-row-${p.id}`}>
                  <td className="px-5 py-3">
                    <div className="text-sm text-foreground font-medium">{p.name}</div>
                    <div className="text-[11px] text-muted-foreground">{p.email}</div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-[11px] text-muted-foreground">{LEVEL_LABELS[p.partner_type] || p.partner_type}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {p.pan_verified
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mx-auto" strokeWidth={2} />
                      : <XCircle className="w-3.5 h-3.5 text-muted-foreground/50 mx-auto" strokeWidth={1.5} />}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {p.bank_verified
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mx-auto" strokeWidth={2} />
                      : <XCircle className="w-3.5 h-3.5 text-muted-foreground/50 mx-auto" strokeWidth={1.5} />}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-medium border ${
                      KYC_STATUS_STYLES[p.kyc_status] || KYC_STATUS_STYLES.pending
                    }`}>
                      {KYC_STATUS_LABELS[p.kyc_status] || p.kyc_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────────

export default function PartnerKYCStatus() {
  const [kycData, setKycData]     = useState(null);
  const [teamData, setTeamData]   = useState(null);
  const [profile, setProfile]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [verifyingPan, setVerifyingPan]   = useState(false);
  const [verifyingBank, setVerifyingBank] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [kycRes, profileRes, teamRes] = await Promise.all([
        axios.get(`${API}/api/sm/partners/portal/kyc/status`, { withCredentials: true }),
        axios.get(`${API}/api/sm/partners/portal/me`, { withCredentials: true }),
        axios.get(`${API}/api/sm/partners/portal/team-kyc`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setKycData(kycRes.data);
      setProfile(profileRes.data);
      setTeamData(teamRes.data);
      setLastRefreshed(new Date());
    } catch (e) {
      console.error("KYC status load error:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 30 seconds for real-time status
    intervalRef.current = setInterval(() => load(true), 30000);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const handleVerifyPAN = async () => {
    setVerifyingPan(true);
    try {
      const r = await axios.post(`${API}/api/sm/partners/portal/kyc/verify-pan`, {}, { withCredentials: true });
      if (r.data.valid) {
        if (r.data.auto_approval?.approved) toast.success("PAN verified — KYC auto-approved! Your account is now active.");
        else toast.success(`PAN verified${r.data.name_on_pan ? ` — ${r.data.name_on_pan}` : ""}`);
        await load(true);
      } else {
        toast.error(r.data.error || "PAN verification failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PAN verification failed");
    } finally { setVerifyingPan(false); }
  };

  const handleVerifyBank = async () => {
    setVerifyingBank(true);
    try {
      const r = await axios.post(`${API}/api/sm/partners/portal/kyc/verify-bank`, {}, { withCredentials: true });
      if (r.data.valid) {
        if (r.data.auto_approval?.approved) toast.success("Bank verified — KYC auto-approved! Your account is now active.");
        else toast.success("Bank account verified successfully");
        await load(true);
      } else {
        toast.error(r.data.error || "Bank verification failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bank verification failed");
    } finally { setVerifyingBank(false); }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-muted rounded-2xl animate-pulse" />)}
      </div>
    );
  }

  const kycStatus  = kycData?.kyc_status || "pending";
  const panVerified  = kycData?.pan_verified  || false;
  const bankVerified = kycData?.bank_verified || false;

  return (
    <div className="space-y-5 max-w-2xl" data-testid="partner-kyc-status-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-light text-foreground tracking-tight">KYC Status</h1>
          <p className="text-sm text-muted-foreground mt-1">Your identity verification journey</p>
        </div>
        <button
          onClick={() => load(true)}
          data-testid="kyc-refresh-btn"
          className="flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground transition-colors mt-1"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {lastRefreshed
            ? `Updated ${lastRefreshed.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata" })}`
            : "Refresh"}
        </button>
      </div>

      {/* Journey */}
      <JourneyTracker kycStatus={kycStatus} panVerified={panVerified} bankVerified={bankVerified} />

      {/* Status Banner */}
      <StatusBanner kycStatus={kycStatus} kycData={kycData} />

      {/* Verification Cards */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-3">Verification Checks</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <VerifyCard
            type="pan"
            verified={panVerified}
            hasDocs={kycData?.has_pan}
            value={profile?.pan_number}
            loading={verifyingPan}
            onVerify={handleVerifyPAN}
          />
          <VerifyCard
            type="bank"
            verified={bankVerified}
            hasDocs={kycData?.has_bank}
            value={kycData?.bank_account_masked ? `${kycData.bank_account_masked}  ${kycData.bank_ifsc || ""}` : null}
            loading={verifyingBank}
            onVerify={handleVerifyBank}
          />
        </div>
      </div>

      {/* What's Next */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-3">What's Next</div>
        <WhatNext
          kycStatus={kycStatus}
          panVerified={panVerified}
          bankVerified={bankVerified}
          hasPan={kycData?.has_pan}
          hasBank={kycData?.has_bank}
        />
      </div>

      {/* Team KYC */}
      {teamData?.can_view_team && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-3">Team KYC Overview</div>
          <TeamKYC teamData={teamData} />
        </div>
      )}
    </div>
  );
}
